import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Upload,
  FileSpreadsheet,
  Play,
  Eye,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  FolderKanban,
  Tag,
  Settings2,
  MessageSquare,
  HardDrive,
  Copy,
  Database,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { BACKEND_CONFIG } from '../config/backend';
import {
  pickFreshestJobs,
  isJobActivelyRunning,
  isJobFinished as jobLooksFinished,
} from '../utils/csvImportJobs';

const API_BASE = `${BACKEND_CONFIG.BASE_URL}/api/bitrix`;
const JOB_STORAGE_KEY = 'csvImportActiveJobId';

function jobToResult(job) {
  const results = job.recentResults || job.results || [];
  const live = summarizeLiveResults(results);
  return {
    async: true,
    jobId: job.jobId,
    processed: job.summary?.processed || job.progress?.processed || 0,
    created: job.summary?.created || job.progress?.created || 0,
    skipped: job.summary?.skipped || job.progress?.skipped || 0,
    errors: job.summary?.errors || job.progress?.errors || 0,
    dryRun: job.summary?.dryRun,
    attachmentsDownloaded:
      job.summary?.attachmentsDownloaded || live.attachmentsDownloaded || 0,
    attachmentsFromMinio: live.fromMinio,
    commentAttachmentsSkipped: live.commentAttSkipped,
    commentAttachmentsDownloaded: live.commentAttDownloaded,
    missingCommentFiles: live.missingCommentFiles,
    backupsSaved: job.summary?.backupsSaved || 0,
    cardsBackupSaved: job.summary?.cardsBackupSaved || 0,
    companiesCreated: job.summary?.companiesCreated || 0,
    contactsCreated: job.summary?.contactsCreated || 0,
    results,
  };
}

/** Agrega métricas dos últimos resultados do job (para o painel ao vivo). */
function summarizeLiveResults(results = []) {
  let fromMinio = 0;
  let attachmentsDownloaded = 0;
  let commentAttSkipped = 0;
  let commentAttDownloaded = 0;
  let missingCommentFiles = 0;
  let duplicates = 0;
  for (const r of results) {
    if (r.status === 'skipped') duplicates += 1;
    fromMinio += Number(r.attachmentsFromMinio || 0);
    attachmentsDownloaded += Number(r.attachmentsDownloaded || 0);
    commentAttSkipped += Number(r.commentSync?.attachmentsSkipped || 0);
    commentAttDownloaded += Number(r.commentSync?.attachmentsDownloaded || 0);
    missingCommentFiles += Array.isArray(r.missingCommentFiles)
      ? r.missingCommentFiles.length
      : 0;
  }
  return {
    fromMinio,
    attachmentsDownloaded,
    commentAttSkipped,
    commentAttDownloaded,
    missingCommentFiles,
    duplicates,
  };
}

function describeRowDetail(r) {
  if (r.status === 'skipped') {
    const why =
      r.reason === 'duplicado'
        ? 'Já existe no Bitrix novo (mesmo ID antigo ou CNPJ nesta pipeline). A opção “Pular duplicados” está ligada — não cria de novo. Comentários em falta ainda são enviados para o card existente.'
        : r.reason || 'Ignorado';
    const extra = [];
    if (r.commentPost?.posted > 0) extra.push(`${r.commentPost.posted} comentário(s) enviados`);
    if (r.commentPost?.filesAttached > 0) extra.push(`${r.commentPost.filesAttached} arquivo(s) na timeline`);
    if (r.commentFilesToField?.attached > 0) extra.push(`${r.commentFilesToField.attached} no campo AnexoS do contrato`);
    if (r.commentPost?.skipped > 0) extra.push(`${r.commentPost.skipped} já estavam no card`);
    return {
      headline: `Duplicado → #${r.existingDealId || '?'}${
        r.existingPipeline != null ? ` · pipeline ${r.existingPipeline}` : ''
      }${extra.length ? ` · ${extra.join(' · ')}` : ''}`,
      why,
    };
  }
  if (r.status === 'error') {
    return { headline: r.error || 'Erro', why: null };
  }
  const parts = [];
  if (r.newDealId) parts.push(`#${r.newDealId}`);
  if (r.attachmentsFromMinio > 0) parts.push(`${r.attachmentsFromMinio} do MinIO`);
  if (r.attachmentsFromCdn > 0) parts.push(`${r.attachmentsFromCdn} do CDN`);
  if (r.attachmentsDownloaded > 0 && !r.attachmentsFromMinio && !r.attachmentsFromCdn) {
    parts.push(`${r.attachmentsDownloaded} anexo(s)`);
  }
  if (r.commentPost) {
    if (r.commentPost.posted > 0) parts.push(`${r.commentPost.posted} comentário(s) na timeline`);
    if (r.commentPost.filesAttached > 0) parts.push(`${r.commentPost.filesAttached} arquivo(s) nos comentários`);
    if (r.commentFilesToField?.attached > 0) parts.push(`${r.commentFilesToField.attached} no AnexoS do contrato`);
    if (r.commentPost.skipped > 0) parts.push(`${r.commentPost.skipped} comentário(s) já existiam`);
    if (r.commentPost.errors > 0) parts.push(`${r.commentPost.errors} comentário(s) com erro`);
  }
  if (r.commentSync) {
    const cs = r.commentSync;
    if (cs.fromDb) parts.push('comentários já no banco');
    else if (cs.commentsInserted > 0) parts.push(`+${cs.commentsInserted} comentário(s) no banco`);
    if (cs.attachmentsSkipped > 0) {
      parts.push(`${cs.attachmentsSkipped} anexo(s) de comentário já no MinIO`);
    }
    if (cs.attachmentsDownloaded > 0) {
      parts.push(`+${cs.attachmentsDownloaded} anexo(s) de comentário baixado(s)`);
    }
    if (cs.attachmentsFailed > 0) {
      parts.push(`${cs.attachmentsFailed} anexo(s) de comentário falhou(aram)`);
    }
  }
  if (r.company?.companyId) parts.push(`empresa #${r.company.companyId}`);
  if (r.contact?.contactId) parts.push(`contato #${r.contact.contactId}`);
  return {
    headline: parts.join(' · ') || '—',
    why: null,
    missing: r.missingCommentFiles || [],
    attachmentErrors: r.attachmentErrors || [],
  };
}

const CsvImportPage = () => {
  const [file, setFile] = useState(null);
  const [csvContent, setCsvContent] = useState('');
  const [fileName, setFileName] = useState('');

  const [pipelines, setPipelines] = useState([]);
  const [stages, setStages] = useState([]);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [loadingStages, setLoadingStages] = useState(false);

  const [sourcePipelines, setSourcePipelines] = useState([]);
  const [sourceStages, setSourceStages] = useState([]);
  const [loadingSourcePipelines, setLoadingSourcePipelines] = useState(false);
  const [loadingSourceStages, setLoadingSourceStages] = useState(false);
  const [dbSource, setDbSource] = useState({ pipeline: '', stage: '' });
  const [dbPreview, setDbPreview] = useState(null);
  const [dbPreviewing, setDbPreviewing] = useState(false);

  const [config, setConfig] = useState({
    targetPipeline: '',
    targetStage: '',
    skipDuplicates: true,
    dryRun: false,
    downloadAttachments: true,
    limit: 0,
  });

  const [preview, setPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [jobProgress, setJobProgress] = useState(null);
  const [activeJobs, setActiveJobs] = useState([]);
  const [resumeJobId, setResumeJobId] = useState('');
  const pollingRef = useRef(false);
  const doneNotifiedRef = useRef(new Set());

  const loadPipelines = useCallback(async () => {
    setLoadingPipelines(true);
    try {
      const res = await fetch(`${API_BASE}/new/pipelines`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setPipelines(data.pipelines || []);
    } catch (err) {
      toast.error(`Erro ao carregar pipelines: ${err.message}`);
    } finally {
      setLoadingPipelines(false);
    }
  }, []);

  const loadSourcePipelines = useCallback(async () => {
    setLoadingSourcePipelines(true);
    try {
      const res = await fetch(`${API_BASE}/import/from-db/pipelines`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setSourcePipelines(data.pipelines || []);
    } catch (err) {
      toast.error(`Erro ao carregar pipelines do backup: ${err.message}`);
    } finally {
      setLoadingSourcePipelines(false);
    }
  }, []);

  const loadStages = useCallback(async (pipelineId) => {
    if (!pipelineId && pipelineId !== 0) {
      setStages([]);
      return;
    }
    setLoadingStages(true);
    try {
      const res = await fetch(`${API_BASE}/new/stages/${pipelineId}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setStages(data.stages || []);
    } catch (err) {
      toast.error(`Erro ao carregar fases: ${err.message}`);
    } finally {
      setLoadingStages(false);
    }
  }, []);

  const loadSourceStages = useCallback(async (pipelineName) => {
    if (!pipelineName) {
      setSourceStages([]);
      return;
    }
    setLoadingSourceStages(true);
    try {
      const res = await fetch(
        `${API_BASE}/import/from-db/stages?pipeline=${encodeURIComponent(pipelineName)}`,
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setSourceStages(data.stages || []);
    } catch (err) {
      toast.error(`Erro ao carregar fases do backup: ${err.message}`);
    } finally {
      setLoadingSourceStages(false);
    }
  }, []);

  useEffect(() => {
    loadPipelines();
    loadSourcePipelines();
  }, [loadPipelines, loadSourcePipelines]);

  useEffect(() => {
    if (config.targetPipeline !== '') {
      loadStages(config.targetPipeline);
    }
  }, [config.targetPipeline, loadStages]);

  useEffect(() => {
    if (dbSource.pipeline) {
      loadSourceStages(dbSource.pipeline);
    } else {
      setSourceStages([]);
    }
  }, [dbSource.pipeline, loadSourceStages]);

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (!selected.name.toLowerCase().endsWith('.csv')) {
      toast.error('Selecione um arquivo .csv exportado do Bitrix');
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const content = ev.target?.result;
      if (typeof content === 'string') {
        setCsvContent(content);
        setFile(selected);
        setFileName(selected.name);
        setPreview(null);
        setImportResult(null);
        toast.success(`Arquivo carregado: ${selected.name}`);
      }
    };
    reader.readAsText(selected, 'UTF-8');
  };

  const pollJobUntilDone = useCallback(async (jobId) => {
    // Se já está acompanhando ESTE job, não reinicia; se era outro/travado, libera
    if (pollingRef.current) {
      if (pollingRef.current === jobId) return null;
      pollingRef.current = false;
    }
    pollingRef.current = jobId;
    localStorage.setItem(JOB_STORAGE_KEY, jobId);
    setResumeJobId(jobId);
    setImporting(true);

    const fetchJobSnapshot = async (id) => {
      try {
        const res = await fetch(`${API_BASE}/import/csv/jobs/${id}`);
        const data = await res.json();
        if (data.success && data.job) return data.job;
      } catch (_) {}
      try {
        const res = await fetch(`/csv-jobs.json?t=${Date.now()}`, { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          const found = (data.jobs || []).find((j) => j?.jobId === id);
          if (found) return found;
        }
      } catch (_) {}
      return null;
    };

    try {
      const maxAttempts = 200000;
      for (let i = 0; i < maxAttempts; i++) {
        // Outro fluxo assumiu o polling
        if (pollingRef.current !== jobId) return null;

        const job = await fetchJobSnapshot(jobId);
        if (!job) {
          localStorage.removeItem(JOB_STORAGE_KEY);
          throw new Error('Job não encontrado (pode ter expirado após restart). Veja o histórico em Resultado se já tiver finalizado.');
        }

        setJobProgress(job);
        setActiveJobs([job]);
        // Atualiza tabela ao vivo (últimos resultados do job)
        setImportResult(jobToResult(job));

        const finished =
          job.status === 'done' ||
          job.status === 'error' ||
          jobLooksFinished(job);

        if (finished && job.status !== 'error') {
          localStorage.removeItem(JOB_STORAGE_KEY);
          const finalJob =
            job.status === 'done'
              ? job
              : { ...job, status: 'done', finishedAt: job.finishedAt || Date.now() };
          const finalResult = jobToResult(finalJob);
          setImportResult(finalResult);
          setJobProgress(finalJob);
          if (!doneNotifiedRef.current.has(finalJob.jobId)) {
            doneNotifiedRef.current.add(finalJob.jobId);
            toast.success(
              `Importação concluída: ${finalResult.created} criado(s), ${finalResult.skipped} duplicado(s) pulado(s), ${finalResult.errors} erro(s)`,
              { autoClose: 6000 },
            );
          }
          return finalResult;
        }

        if (job.status === 'error') {
          localStorage.removeItem(JOB_STORAGE_KEY);
          throw new Error(job.error || 'Importação falhou no servidor');
        }

        await new Promise((r) => setTimeout(r, 3000));
      }
      throw new Error('Tempo esgotado aguardando a importação');
    } finally {
      if (pollingRef.current === jobId) {
        pollingRef.current = false;
      }
      setImporting(false);
    }
  }, []);

  const dismissFinishedJob = useCallback(() => {
    pollingRef.current = false;
    localStorage.removeItem(JOB_STORAGE_KEY);
    setImporting(false);
    setJobProgress(null);
    setActiveJobs([]);
    setResumeJobId('');
  }, []);

  // Puxa jobs a cada 3s — prioriza API; JSON estático não sobrescreve done
  useEffect(() => {
    let cancelled = false;
    const maxProcessedByJob = new Map();

    const loadJobs = async () => {
      const candidates = [];
      const storedId = localStorage.getItem(JOB_STORAGE_KEY);
      let apiOk = false;

      const pushFromApi = async (url) => {
        try {
          const res = await fetch(url);
          if (!res.ok) return;
          const data = await res.json();
          if (data.success && Array.isArray(data.jobs)) {
            apiOk = true;
            candidates.push(...data.jobs);
          }
          if (data.success && data.job) {
            apiOk = true;
            candidates.push(data.job);
          }
        } catch {
          // ignore
        }
      };

      await Promise.all([
        pushFromApi(`${API_BASE}/import/csv/jobs?active=1`),
        pushFromApi(`${API_BASE}/import/csv/jobs`),
        storedId
          ? (async () => {
              try {
                const res = await fetch(`${API_BASE}/import/csv/jobs/${storedId}`);
                if (!res.ok) {
                  if (res.status === 404) localStorage.removeItem(JOB_STORAGE_KEY);
                  return;
                }
                const data = await res.json();
                if (data.success && data.job) {
                  apiOk = true;
                  candidates.push(data.job);
                }
              } catch {
                // ignore
              }
            })()
          : Promise.resolve(),
      ]);

      // JSON estático: sempre mesclado depois (pickFreshest prefere done),
      // ou sozinho se a API estiver fora.
      try {
        const res = await fetch(`/csv-jobs.json?t=${Date.now()}`, { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data.jobs)) candidates.push(...data.jobs);
        }
      } catch {
        // ignore
      }

      if (cancelled) return;
      if (!apiOk && candidates.length === 0) return;

      const jobs = pickFreshestJobs(candidates)
        .map((job) => {
          const floor = maxProcessedByJob.get(job.jobId) || 0;
          const processed = Number(job.progress?.processed || 0);
          if (processed >= floor) maxProcessedByJob.set(job.jobId, processed);
          if (processed < floor && job.status === 'running' && job.progress) {
            return {
              ...job,
              progress: {
                ...job.progress,
                processed: floor,
                percent:
                  job.progress.total > 0
                    ? Math.min(100, Math.round((floor / job.progress.total) * 100))
                    : job.progress.percent,
              },
            };
          }
          if (jobLooksFinished(job) && job.status === 'running') {
            return { ...job, status: 'done', finishedAt: job.finishedAt || Date.now() };
          }
          return job;
        })
        .sort(
          (a, b) =>
            (b.finishedAt || b.startedAt || b.createdAt || 0) -
            (a.finishedAt || a.startedAt || a.createdAt || 0),
        );

      const active = jobs.filter((j) => isJobActivelyRunning(j));
      const latestActive = active[0] || null;
      const latestDone = jobs.find((j) => j.status === 'done') || null;

      if (latestActive) {
        setActiveJobs(active);
        setJobProgress((prev) => {
          if (!prev || prev.jobId !== latestActive.jobId) return latestActive;
          const prevP = Number(prev.progress?.processed || 0);
          const nextP = Number(latestActive.progress?.processed || 0);
          if (nextP < prevP) return prev;
          return latestActive;
        });
        setImportResult(jobToResult(latestActive));
        setResumeJobId(latestActive.jobId);
        setImporting(true);
        localStorage.setItem(JOB_STORAGE_KEY, latestActive.jobId);
        return;
      }

      setImporting(false);
      localStorage.removeItem(JOB_STORAGE_KEY);

      if (latestDone) {
        setActiveJobs([latestDone]);
        setJobProgress(latestDone);
        setResumeJobId(latestDone.jobId);
        if (!doneNotifiedRef.current.has(latestDone.jobId)) {
          doneNotifiedRef.current.add(latestDone.jobId);
          setImportResult(jobToResult(latestDone));
          const created = latestDone.summary?.created ?? latestDone.progress?.created ?? 0;
          const skipped = latestDone.summary?.skipped ?? latestDone.progress?.skipped ?? 0;
          const errors = latestDone.summary?.errors ?? latestDone.progress?.errors ?? 0;
          toast.success(
            `Importação concluída: ${created} criado(s), ${skipped} duplicado(s) pulado(s), ${errors} erro(s)`,
            { autoClose: 6000 },
          );
        } else {
          setImportResult((prev) => prev || jobToResult(latestDone));
        }
        return;
      }

      const latestError = jobs.find((j) => j.status === 'error');
      if (latestError) {
        setActiveJobs([latestError]);
        setJobProgress(latestError);
        setResumeJobId(latestError.jobId);
      } else {
        setActiveJobs([]);
      }
    };

    loadJobs();
    const timer = setInterval(loadJobs, 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const handlePreview = async () => {
    if (!csvContent) {
      toast.warning('Selecione um arquivo CSV primeiro');
      return;
    }

    setPreviewing(true);
    setPreview(null);
    try {
      const res = await fetch(`${API_BASE}/import/csv/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          csvContent,
          limit: config.limit || 5,
          targetPipeline: config.targetPipeline !== '' ? config.targetPipeline : null,
        }),
      });
      if (res.status === 413) {
        throw new Error('Arquivo muito grande para o servidor (limite de upload). Contate o admin.');
      }
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setPreview(data);
      toast.success(`Pré-visualização: ${data.previews?.length || 0} linha(s)`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPreviewing(false);
    }
  };

  const handleResumeJob = async () => {
    const jobId = (resumeJobId || '').trim();
    if (!jobId) {
      toast.warning('Informe o ID do job para acompanhar');
      return;
    }
    try {
      await pollJobUntilDone(jobId);
    } catch (err) {
      toast.error(err.message);
      setImporting(false);
      pollingRef.current = false;
    }
  };

  const handleImport = async () => {
    if (!csvContent) {
      toast.warning('Selecione um arquivo CSV primeiro');
      return;
    }
    if (config.targetPipeline === '' || !config.targetStage) {
      toast.warning('Selecione pipeline e fase de destino no Bitrix novo');
      return;
    }

    // Libera trava de job anterior já finalizado
    if (isJobActivelyRunning(jobProgress)) {
      toast.warning('Já existe uma importação em andamento. Aguarde terminar.');
      return;
    }
    pollingRef.current = false;
    localStorage.removeItem(JOB_STORAGE_KEY);

    setImporting(true);
    setImportResult(null);
    setJobProgress(null);
    setActiveJobs([]);
    try {
      const res = await fetch(`${API_BASE}/import/csv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          csvContent,
          targetPipeline: config.targetPipeline,
          targetStage: config.targetStage,
          skipDuplicates: config.skipDuplicates,
          dryRun: config.dryRun,
          downloadAttachments: config.downloadAttachments,
          limit: config.limit > 0 ? config.limit : null,
          async: true,
        }),
      });
      if (res.status === 413) {
        throw new Error(
          'Arquivo muito grande (~57MB). O limite do servidor precisa ser aumentado (já em correção).',
        );
      }
      const data = await res.json();
      if (!data.success) throw new Error(data.message);

      if (data.async && data.jobId) {
        toast.info(`Importação em background iniciada (job ${data.jobId})`, { autoClose: 4000 });
        await pollJobUntilDone(data.jobId);
        return;
      }

      setImportResult(data);
      toast.success(
        `Importação: ${data.created} criado(s), ${data.skipped} duplicado(s) pulado(s), ${data.errors} erro(s)`,
        { autoClose: 5000 },
      );
    } catch (err) {
      toast.error(err.message);
    } finally {
      // Se não ficou um poll ativo, libera o botão
      if (!pollingRef.current) {
        setImporting(false);
      }
    }
  };

  const handleDbPreview = async () => {
    if (!dbSource.pipeline || !dbSource.stage) {
      toast.warning('Selecione pipeline e fase de origem no backup');
      return;
    }
    setDbPreviewing(true);
    try {
      const qs = new URLSearchParams({
        pipeline: dbSource.pipeline,
        stage: dbSource.stage,
        limit: '5',
      });
      const res = await fetch(`${API_BASE}/import/from-db/preview?${qs}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setDbPreview(data);
      toast.success(`${data.count} card(s) encontrados no backup`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDbPreviewing(false);
    }
  };

  const handleDbImport = async () => {
    if (!dbSource.pipeline || !dbSource.stage) {
      toast.warning('Selecione pipeline e fase de origem no backup');
      return;
    }
    if (config.targetPipeline === '' || !config.targetStage) {
      toast.warning('Selecione pipeline e fase de destino no Bitrix novo');
      return;
    }
    if (isJobActivelyRunning(jobProgress)) {
      toast.warning('Já existe uma importação em andamento. Aguarde terminar.');
      return;
    }
    pollingRef.current = false;
    localStorage.removeItem(JOB_STORAGE_KEY);

    setImporting(true);
    setImportResult(null);
    setJobProgress(null);
    setActiveJobs([]);
    try {
      const res = await fetch(`${API_BASE}/import/from-db`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourcePipeline: dbSource.pipeline,
          sourceStage: dbSource.stage,
          targetPipeline: config.targetPipeline,
          targetStage: config.targetStage,
          skipDuplicates: config.skipDuplicates,
          dryRun: config.dryRun,
          downloadAttachments: config.downloadAttachments,
          limit: config.limit > 0 ? config.limit : null,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);

      toast.info(
        `Migração do backup iniciada: ${data.sourceCount} card(s) · job ${data.jobId}`,
        { autoClose: 5000 },
      );
      await pollJobUntilDone(data.jobId);
    } catch (err) {
      toast.error(err.message);
    } finally {
      if (!pollingRef.current) {
        setImporting(false);
      }
    }
  };

  const isJobFinished =
    jobLooksFinished(jobProgress) ||
    jobProgress?.status === 'done' ||
    jobProgress?.status === 'error';
  const hasActiveJob = isJobActivelyRunning(jobProgress);
  const importBlocked =
    !csvContent || !config.targetStage || hasActiveJob || (importing && !isJobFinished);
  const dbImportBlocked =
    !dbSource.pipeline ||
    !dbSource.stage ||
    !config.targetStage ||
    hasActiveJob ||
    (importing && !isJobFinished);

  const statusIcon = (status) => {
    if (status === 'created' || status === 'dry_run') return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    if (status === 'skipped') return <AlertTriangle className="w-4 h-4 text-amber-600" />;
    return <XCircle className="w-4 h-4 text-red-600" />;
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">Importação CSV → Bitrix Novo</h1>
        <p className="text-gray-600 mt-1">
          Importe negócios exportados do Bitrix antigo usando os mapeamentos já configurados
        </p>
      </div>

      {/* Progresso / status do job — todos os usuários */}
      {(jobProgress || activeJobs.length > 0) && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className={
            jobProgress?.status === 'done'
              ? 'bg-emerald-50 rounded-xl border-2 border-emerald-300 p-5 shadow-sm'
              : jobProgress?.status === 'error'
                ? 'bg-red-50 rounded-xl border-2 border-red-300 p-5 shadow-sm'
                : 'bg-sky-50 rounded-xl border-2 border-sky-300 p-5 shadow-sm'
          }
        >
          <div className="flex items-center justify-between gap-3 mb-2">
            <h2
              className={
                jobProgress?.status === 'done'
                  ? 'text-lg font-bold text-emerald-900'
                  : jobProgress?.status === 'error'
                    ? 'text-lg font-bold text-red-900'
                    : 'text-lg font-bold text-sky-900'
              }
            >
              {jobProgress?.status === 'done'
                ? 'Importação concluída com sucesso'
                : jobProgress?.status === 'error'
                  ? 'Importação com erro'
                  : 'Importação em andamento'}
            </h2>
            <span
              className={
                jobProgress?.status === 'done'
                  ? 'text-sm font-mono text-emerald-800'
                  : 'text-sm font-mono text-sky-800'
              }
            >
              {jobProgress?.jobId || resumeJobId || '…'}
            </span>
          </div>
          <p
            className={
              jobProgress?.status === 'done'
                ? 'text-sm text-emerald-800 mb-2'
                : jobProgress?.status === 'error'
                  ? 'text-sm text-red-800 mb-2'
                  : 'text-sm text-sky-800 mb-2'
            }
          >
            Status: <strong>{jobProgress?.status || 'conectando…'}</strong>
            {' · '}Processados:{' '}
            {jobProgress?.progress?.processed || 0}/{jobProgress?.progress?.total || '?'}
            {' '}({jobProgress?.progress?.percent || 0}%)
            {(jobProgress?.summary?.targetPipeline ?? jobProgress?.targetPipeline) != null && (
              <span> · pipeline {jobProgress?.summary?.targetPipeline ?? jobProgress?.targetPipeline}</span>
            )}
          </p>
          <div
            className={
              jobProgress?.status === 'done'
                ? 'w-full h-3 bg-emerald-100 rounded-full overflow-hidden mb-2'
                : 'w-full h-3 bg-sky-100 rounded-full overflow-hidden mb-2'
            }
          >
            <div
              className={
                jobProgress?.status === 'done'
                  ? 'h-full bg-emerald-600 transition-all duration-500'
                  : 'h-full bg-sky-600 transition-all duration-500'
              }
              style={{ width: `${jobProgress?.progress?.percent || 0}%` }}
            />
          </div>
          <p
            className={
              jobProgress?.status === 'done' ? 'text-xs text-emerald-700' : 'text-xs text-sky-700'
            }
          >
            Criados:{' '}
            {jobProgress?.summary?.created ?? jobProgress?.progress?.created ?? 0}
            {' · '}Duplicados pulados:{' '}
            {jobProgress?.summary?.skipped ?? jobProgress?.progress?.skipped ?? 0}
            {' · '}Erros:{' '}
            {jobProgress?.summary?.errors ?? jobProgress?.progress?.errors ?? 0}
            {jobProgress?.status !== 'done' && (
              <span className="opacity-70">
                {' '}
                (processados = criados + duplicados + erros)
              </span>
            )}
            {jobProgress?.progress?.currentTitle
              ? ` · Atual: ${jobProgress.progress.currentTitle}`
              : ''}
          </p>

          {(() => {
            const live = summarizeLiveResults(
              jobProgress?.recentResults || importResult?.results || [],
            );
            if (!live.fromMinio && !live.commentAttSkipped && !live.duplicates) return null;
            return (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                <div className="flex items-start gap-2 rounded-lg bg-white/70 border border-sky-200/80 px-3 py-2">
                  <Copy className="w-3.5 h-3.5 mt-0.5 text-amber-600 shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-800">Por que “pula”?</p>
                    <p className="text-slate-600 leading-snug">
                      Duplicado = já existe deal com o mesmo ID antigo ou CNPJ nesta pipeline.
                      A opção “Pular duplicados” evita criar de novo.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2 rounded-lg bg-white/70 border border-sky-200/80 px-3 py-2">
                  <HardDrive className="w-3.5 h-3.5 mt-0.5 text-indigo-600 shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-800">Anexos (últimos cards)</p>
                    <p className="text-slate-600 leading-snug">
                      {live.fromMinio} do MinIO · {live.commentAttSkipped} de comentário já
                      no backup (não baixa de novo)
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2 rounded-lg bg-white/70 border border-sky-200/80 px-3 py-2">
                  <MessageSquare className="w-3.5 h-3.5 mt-0.5 text-violet-600 shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-800">Comentários</p>
                    <p className="text-slate-600 leading-snug">
                      {live.commentAttDownloaded} anexo(s) baixado(s) agora
                      {live.missingCommentFiles > 0
                        ? ` · ${live.missingCommentFiles} faltando (404 no Disk antigo)`
                        : ''}
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}
          {jobProgress?.status === 'done' && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <p className="text-sm font-medium text-emerald-800">
                Job finalizado — você já pode iniciar outra importação.
              </p>
              <button
                type="button"
                onClick={dismissFinishedJob}
                className="text-sm px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Nova importação
              </button>
            </div>
          )}
          {jobProgress?.status === 'error' && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <p className="text-sm font-medium text-red-800">
                Job com erro — você pode tentar novamente.
              </p>
              <button
                type="button"
                onClick={dismissFinishedJob}
                className="text-sm px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                Limpar e tentar de novo
              </button>
            </div>
          )}
          {activeJobs.length > 1 && (
            <ul className="mt-3 text-xs text-sky-800 space-y-1">
              {activeJobs.map((j) => (
                <li key={j.jobId} className="font-mono">
                  {j.jobId} · {j.status} · {j.progress?.percent || 0}%
                </li>
              ))}
            </ul>
          )}
        </motion.div>
      )}

      {/* Retomar job se a barra não apareceu sozinha */}
      <div className="bg-white rounded-xl border border-amber-200 p-4 flex flex-col md:flex-row gap-3 items-stretch md:items-end">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Acompanhar job em andamento (após F5)
          </label>
          <input
            type="text"
            value={resumeJobId}
            onChange={(e) => setResumeJobId(e.target.value)}
            placeholder="Cole o jobId, ex: 418229679355eddb"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
          />
        </div>
        <button
          type="button"
          onClick={handleResumeJob}
          disabled={hasActiveJob}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 min-h-[44px]"
        >
          Retomar acompanhamento
        </button>
      </div>

      {/* Migrar direto do backup PostgreSQL */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl shadow-lg border border-slate-700 p-6 text-white"
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-lg bg-cyan-500/20 border border-cyan-400/40 flex items-center justify-center">
            <Database className="w-5 h-5 text-cyan-300" />
          </div>
          <div>
            <h2 className="text-lg font-bold">Migrar do backup (banco)</h2>
            <p className="text-sm text-slate-300">
              Puxa cards de <span className="font-mono text-cyan-200">bitrix_old.cards</span> — sem
              gerar CSV manual. Destino = pipeline/fase do Bitrix novo abaixo.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-1">
              Pipeline de origem (antigo)
            </label>
            <select
              value={dbSource.pipeline}
              onChange={(e) => {
                setDbSource({ pipeline: e.target.value, stage: '' });
                setDbPreview(null);
              }}
              disabled={loadingSourcePipelines}
              className="w-full px-4 py-2.5 rounded-lg bg-slate-950/60 border border-slate-600 text-white focus:ring-2 focus:ring-cyan-500 min-h-[44px]"
            >
              <option value="">Selecione...</option>
              {sourcePipelines.map((p) => (
                <option key={p.name} value={p.name}>
                  {p.name} ({p.count})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-1">
              Fase de origem (antigo)
            </label>
            <select
              value={dbSource.stage}
              onChange={(e) => {
                setDbSource({ ...dbSource, stage: e.target.value });
                setDbPreview(null);
              }}
              disabled={!dbSource.pipeline || loadingSourceStages}
              className="w-full px-4 py-2.5 rounded-lg bg-slate-950/60 border border-slate-600 text-white focus:ring-2 focus:ring-cyan-500 min-h-[44px]"
            >
              <option value="">Selecione...</option>
              {sourceStages.map((s) => (
                <option key={s.name} value={s.name}>
                  {s.name} ({s.count})
                </option>
              ))}
            </select>
          </div>
        </div>

        {dbPreview && (
          <div className="mb-4 rounded-lg bg-slate-950/50 border border-slate-600 px-4 py-3 text-sm">
            <p className="font-medium text-cyan-200">
              {dbPreview.count} card(s) em {dbPreview.pipeline} / {dbPreview.stage}
            </p>
            {dbPreview.sample?.length > 0 && (
              <ul className="mt-2 space-y-1 text-slate-300 font-mono text-xs">
                {dbPreview.sample.map((row) => (
                  <li key={row.id}>
                    #{row.id} — {row.title || '(sem título)'}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleDbPreview}
            disabled={!dbSource.pipeline || !dbSource.stage || dbPreviewing}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 disabled:opacity-50 min-h-[44px]"
          >
            {dbPreviewing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
            Contar / pré-visualizar
          </button>
          <button
            type="button"
            onClick={handleDbImport}
            disabled={dbImportBlocked}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-cyan-500 text-slate-950 font-semibold hover:bg-cyan-400 disabled:opacity-50 min-h-[44px]"
          >
            {importing && !csvContent ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            Iniciar migração do banco
          </button>
        </div>
        {!config.targetStage && (
          <p className="mt-3 text-xs text-amber-300">
            Selecione também o destino no Bitrix novo (pipeline + fase) na seção abaixo.
          </p>
        )}
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg border border-gray-200 p-6"
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Upload className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Arquivo CSV</h2>
              <p className="text-sm text-gray-500">Export do Bitrix antigo (separador ;)</p>
            </div>
          </div>

          <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-indigo-200 rounded-xl cursor-pointer bg-indigo-50/50 hover:bg-indigo-50 transition-colors">
            <FileSpreadsheet className="w-10 h-10 text-indigo-500 mb-2" />
            <span className="text-sm font-medium text-indigo-700">
              {fileName || 'Clique para selecionar o CSV'}
            </span>
            <span className="text-xs text-gray-500 mt-1">DEAL_*.csv do Bitrix</span>
            <input type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
          </label>

          {file && (
            <p className="mt-3 text-sm text-gray-600">
              Tamanho: {file.size > 1024 * 1024
                ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
                : `${(file.size / 1024).toFixed(1)} KB`}
            </p>
          )}
        </motion.div>

        {/* Destino */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-xl shadow-lg border border-gray-200 p-6"
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-lg bg-emerald-600 flex items-center justify-center">
              <FolderKanban className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Destino no Bitrix Novo</h2>
              <p className="text-sm text-gray-500">Pipeline e fase dos negócios criados</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline</label>
              <select
                value={config.targetPipeline}
                onChange={(e) => setConfig({ ...config, targetPipeline: e.target.value, targetStage: '' })}
                disabled={loadingPipelines}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">Selecione...</option>
                {pipelines.map((p) => (
                  <option key={p.ID ?? p.id} value={p.ID ?? p.id}>
                    {p.NAME || p.name} (ID: {p.ID ?? p.id})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Tag className="w-3.5 h-3.5" /> Fase
              </label>
              <select
                value={config.targetStage}
                onChange={(e) => setConfig({ ...config, targetStage: e.target.value })}
                disabled={!config.targetPipeline || loadingStages}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">Selecione...</option>
                {stages.map((s) => (
                  <option key={s.STATUS_ID} value={s.STATUS_ID}>
                    {s.NAME} ({s.STATUS_ID})
                  </option>
                ))}
              </select>
              <label className="block text-xs font-medium text-gray-500 mt-2 mb-1">
                Ou cole o ID da fase (ex.: C75:EXECUTING)
              </label>
              <input
                type="text"
                value={config.targetStage}
                onChange={(e) => setConfig({ ...config, targetStage: e.target.value.trim() })}
                placeholder="C75:EXECUTING"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Ou cole o ID da pipeline / entity type (ex.: 75)
              </label>
              <input
                type="text"
                value={config.targetPipeline}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    targetPipeline: e.target.value.trim(),
                    targetStage: config.targetStage,
                  })
                }
                placeholder="75"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Opções */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-lg border border-gray-200 p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <Settings2 className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-bold text-gray-900">Opções de importação</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={config.skipDuplicates}
              onChange={(e) => setConfig({ ...config, skipDuplicates: e.target.checked })}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">Pular duplicados (ID antigo ou CNPJ)</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={config.dryRun}
              onChange={(e) => setConfig({ ...config, dryRun: e.target.checked })}
              className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
            />
            <span className="text-sm text-gray-700">Apenas simular (não criar no Bitrix)</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={config.downloadAttachments}
              onChange={(e) => setConfig({ ...config, downloadAttachments: e.target.checked })}
              className="rounded border-gray-300 text-sky-600 focus:ring-sky-500"
            />
            <span className="text-sm text-gray-700">
              Baixar anexos dos links do CSV e anexar no Bitrix novo
            </span>
          </label>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Limite de linhas (teste)
            </label>
            <input
              type="number"
              min="0"
              value={config.limit}
              onChange={(e) => setConfig({ ...config, limit: Number(e.target.value) })}
              placeholder="0 = todas"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            <p className="text-xs text-gray-500 mt-1">0 = todas as linhas · planilhas grandes rodam em background</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-6">
          <button
            type="button"
            onClick={handlePreview}
            disabled={!csvContent || previewing}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 min-h-[44px]"
          >
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            Pré-visualizar
          </button>

          <button
            type="button"
            onClick={handleImport}
            disabled={importBlocked}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 min-h-[44px]"
          >
            {hasActiveJob || (importing && !isJobFinished) ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            {config.dryRun ? 'Simular importação' : 'Importar para o Bitrix novo'}
          </button>
        </div>
      </motion.div>

      {/* Preview */}
      {preview && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-xl shadow-lg border border-indigo-200 p-6"
        >
          <h3 className="text-lg font-bold text-indigo-800 mb-2">Pré-visualização</h3>
          <p className="text-sm text-gray-600 mb-4">
            {preview.totalRows} linhas no CSV · {preview.mappedColumnCount} colunas mapeadas de {preview.columnCount}
            {config.targetPipeline !== '' && (
              <span> · duplicidade verificada só na pipeline <strong>{config.targetPipeline}</strong></span>
            )}
          </p>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-2">ID antigo</th>
                  <th className="text-left p-2">Nome</th>
                  <th className="text-left p-2">CNPJ</th>
                  <th className="text-left p-2">Renda</th>
                  <th className="text-left p-2">Produto</th>
                  <th className="text-left p-2">Empresa / Contato</th>
                  <th className="text-left p-2">Anexo (link)</th>
                  <th className="text-left p-2">Duplicado?</th>
                </tr>
              </thead>
              <tbody>
                {preview.previews?.map((row, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="p-2 font-mono">{row.oldId}</td>
                    <td className="p-2 max-w-xs truncate">{row.title}</td>
                    <td className="p-2">{row.cnpj || '—'}</td>
                    <td className="p-2">{row.opportunity}</td>
                    <td className="p-2 max-w-xs truncate">{row.produto || '—'}</td>
                    <td className="p-2 max-w-xs truncate text-xs">
                      {row.hasCompany && <span className="block text-violet-700">Empresa: {row.companyName || 'sim'}</span>}
                      {row.hasContact && <span className="block text-blue-700">Contato: {row.contactName || 'sim'}</span>}
                      {!row.hasCompany && !row.hasContact && '—'}
                    </td>
                    <td className="p-2 max-w-xs truncate text-sky-700" title={row.linkAnexo}>
                      {row.attachmentUrls?.length
                        ? `${row.attachmentUrls.length} link(s)`
                        : '—'}
                    </td>
                    <td className="p-2">
                      {row.duplicate ? (
                        <span className="text-amber-700">
                          Sim (#{row.duplicate.id}
                          {row.duplicate.pipeline != null ? ` · pipeline ${row.duplicate.pipeline}` : ''})
                        </span>
                      ) : (
                        <span className="text-green-700">Não</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* Resultado / feed ao vivo */}
      {importResult && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-xl shadow-lg border border-emerald-200 p-6"
        >
          <h3 className="text-lg font-bold text-emerald-800 mb-2">
            {jobProgress && !isJobFinished
              ? 'Atividade ao vivo (últimos cards)'
              : 'Resultado da importação'}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-4">
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <p className="text-2xl font-bold">{importResult.processed}</p>
              <p className="text-xs text-gray-600">Processados</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-green-700">{importResult.created}</p>
              <p className="text-xs text-gray-600">{importResult.dryRun ? 'Simulados' : 'Criados'}</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg text-center" title="Já existiam no Bitrix novo (ID antigo ou CNPJ)">
              <p className="text-2xl font-bold text-amber-700">{importResult.skipped}</p>
              <p className="text-xs text-gray-600">Duplicados</p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-red-700">{importResult.errors}</p>
              <p className="text-xs text-gray-600">Erros</p>
            </div>
            <div className="p-3 bg-sky-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-sky-700">{importResult.attachmentsDownloaded || 0}</p>
              <p className="text-xs text-gray-600">Anexos no deal</p>
            </div>
            <div className="p-3 bg-indigo-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-indigo-700">{importResult.attachmentsFromMinio || 0}</p>
              <p className="text-xs text-gray-600">Via MinIO</p>
            </div>
            <div className="p-3 bg-violet-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-violet-700">
                {importResult.commentAttachmentsSkipped || 0}
              </p>
              <p className="text-xs text-gray-600">Coment. já no backup</p>
            </div>
          </div>

          <p className="text-xs text-slate-600 mb-3 leading-relaxed">
            <strong>Duplicados:</strong> o card do CSV já tem negócio no Bitrix novo com o mesmo
            ID antigo (<code>UF_CRM_1760710248</code>) ou o mesmo CNPJ nesta pipeline — por isso
            não cria de novo. <strong>Anexos de comentário “já no backup”</strong> não é erro:
            o arquivo já está no MinIO e só é anexado no deal, sem baixar outra vez.
          </p>

          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b">
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">ID antigo</th>
                  <th className="text-left p-2">Nome</th>
                  <th className="text-left p-2">Detalhe</th>
                </tr>
              </thead>
              <tbody>
                {[...(importResult.results || [])].reverse().map((r, i) => {
                  const detail = describeRowDetail(r);
                  return (
                    <tr key={`${r.oldId}-${i}`} className="border-b align-top">
                      <td className="p-2">
                        <div className="flex items-center gap-1">
                          {statusIcon(r.status)}
                          <span>
                            {r.status === 'skipped'
                              ? 'duplicado'
                              : r.status === 'created'
                                ? 'criado'
                                : r.status}
                          </span>
                        </div>
                      </td>
                      <td className="p-2 font-mono">{r.oldId}</td>
                      <td className="p-2 max-w-xs truncate">{r.title}</td>
                      <td className="p-2 text-xs">
                        <span className="text-slate-800">{detail.headline}</span>
                        {detail.why && (
                          <span className="block text-amber-800 mt-0.5">{detail.why}</span>
                        )}
                        {detail.missing?.length > 0 && (
                          <span className="block text-amber-700 mt-0.5">
                            {detail.missing.length} arquivo(s) de comentário faltando no Disk
                            antigo
                          </span>
                        )}
                        {detail.attachmentErrors?.length > 0 && (
                          <span className="block text-amber-700 mt-0.5">
                            {detail.attachmentErrors.length} anexo(s) com erro no CDN
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* Mapeamentos */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 text-sm text-slate-700">
        <p className="font-semibold mb-2">Campos mapeados nesta versão</p>
        <p className="text-xs leading-relaxed">
          ID → UF_CRM_1760710248 · CNPJ* → UF_CRM_1745494235 · Produto → UF_CRM_1746149585 ·
          Resumo de Vendas → UF_CRM_1746406184 · Link anexo → UF_CRM_1754058580 ·
          Anexos (URL) → baixados, anexados em UF_CRM_1750337214 e salvos no MinIO + PostgreSQL (bitrix_backup) ·
          Dados completos da linha (Empresa:, Contato:, etc.) → bitrix_old.cards + bitrix_new.cards ·
          Colunas de link: Documentos, Anexos Internos, Link anexo contrato, Anexo do Contrato ·
          Responsável → ASSIGNED_BY_ID + UF_CRM_1731077281 · Advogado → UF_CRM_1687540739 ·
          Integração Receita (bloco completo) · e demais colunas com mesmo nome no Bitrix novo.
        </p>
      </div>
    </div>
  );
};

export default CsvImportPage;
